import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import * as Sentry from "@sentry/nextjs";

import { trackLoggedError } from "./analytics";
import { CancelledError, OtpStateError } from "./errors";
import { safeStringify } from "./safeStringify";
import { SENSITIVE_KEYS_WEB, stripEmbeddedValues } from "./sensitiveKeys";

/**
 * Structured error logger.
 *
 * Why this exists: `console.error(message, error)` is the dominant pattern in
 * catch blocks, but Mixpanel session-replay captures console arguments via
 * `String()` coercion — and plain objects (notably RTK Query errors, shape
 * `{ status, data }`) become the literal text "[object Object]". The replay
 * loses everything that matters for diagnosis.
 *
 * `logError` pre-serializes the error into a JSON-stringified payload so the
 * replay shows the HTTP status, the BFF `ExceptionDetail.message`, the spanId,
 * etc. — directly correlatable to BFF Pino logs.
 *
 * Routing:
 *   - Plain `console.error(message, payload)` is always called — that's the
 *     surface Mixpanel captures and the one developers read in DevTools.
 *   - `Error` instances (not RTK Query errors) are routed to
 *     `Sentry.captureException` because `baseQuery.ts` only auto-captures
 *     RTK errors via `captureRtkQueryError`. Plain `Error` from other code
 *     paths would otherwise be invisible to Sentry.
 *   - RTK Query `FetchBaseQueryError` is NOT routed to Sentry from here —
 *     `apps/web/redux/services/baseQuery.ts` already handled it. Double-capture
 *     would inflate quota and confuse fingerprinting.
 */

export type ErrorContext = Record<string, unknown>;

interface SerializedError {
  name?: string;
  message?: string;
  stack?: string;
  cause?: unknown;
  rtkQueryError?: true;
  status?: number | string;
  kind?: "FETCH_ERROR" | "PARSING_ERROR" | "TIMEOUT_ERROR" | "CUSTOM_ERROR";
  data?: unknown;
}

const MAX_DEPTH = 8;
const REDACTED = "[REDACTED]";

function redactInner(
  value: unknown,
  depth: number,
  seen: WeakSet<object>
): unknown {
  if (depth > MAX_DEPTH) return "[MAX_DEPTH]";
  if (value === null || value === undefined) return value;
  // Strings are redacted by SHAPE, not by key: an upstream Bean-Validation
  // message carries the rejected value inline (`… (value=REVOLT21 )`) under
  // the perfectly innocent key `messages`, so key-matching alone would ship a
  // customer's BIC/IBAN to the replay console, Kibana and the Sentry
  // breadcrumb trail. See stripEmbeddedValues.
  if (typeof value === "string") return stripEmbeddedValues(value);
  if (typeof value !== "object") return value;
  // Detect cycles before recursing so we emit a stable "[Circular]" sentinel
  // instead of bottoming out at MAX_DEPTH (which silently truncates and
  // produces a much harder-to-read payload).
  if (seen.has(value as object)) return "[Circular]";
  seen.add(value as object);
  if (Array.isArray(value)) {
    return value.map((v) => redactInner(v, depth + 1, seen));
  }
  const result: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (SENSITIVE_KEYS_WEB.has(k.toLowerCase())) {
      result[k] = REDACTED;
    } else {
      result[k] = redactInner(v, depth + 1, seen);
    }
  }
  return result;
}

/**
 * Redact a value for telemetry: sensitive KEYS (`SENSITIVE_KEYS_WEB`) are
 * replaced wholesale, and every string is additionally scrubbed by SHAPE
 * (`stripEmbeddedValues`) because upstream Bean-Validation messages carry the
 * rejected value inline under a non-sensitive key.
 *
 * Exported because `redux/services/sentryErrorCapture.ts` needs the identical
 * treatment: it puts the request body, the response body and the raw RTK error
 * onto the Sentry scope, and those are the richest customer-data payloads the
 * app produces. Both consumers must share one definition — two drifting
 * redactors is how the gap reappears.
 */
export function redact(value: unknown): unknown {
  return redactInner(value, 0, new WeakSet<object>());
}

/**
 * Distil an error into a small, PII-safe property bag for the Mixpanel
 * "Error Logged" event: HTTP status, the upstream exception type/origin, the
 * error's constructor name, and the current pathname (never the query string).
 * Deliberately carries NO request/response bodies or free-form data — those may
 * contain BFF-filtered customer fields.
 */
function summarizeErrorForAnalytics(error: unknown): Record<string, unknown> {
  const props: Record<string, unknown> = {};
  if (typeof window !== "undefined") {
    props.pathname = window.location.pathname;
  }
  if (isRtkQueryError(error)) {
    props.status = error.status;
    const data = error.data;
    if (data && typeof data === "object") {
      const detail = data as { type?: unknown; origin?: unknown };
      if (typeof detail.type === "string") props.exceptionType = detail.type;
      if (typeof detail.origin === "string") props.origin = detail.origin;
    }
  } else if (error instanceof Error) {
    props.errorName = error.name;
  }
  return props;
}

function isRtkQueryError(err: unknown): err is FetchBaseQueryError {
  if (!err || typeof err !== "object") return false;
  // FetchBaseQueryError always carries a `status` field — number for HTTP
  // responses, a known string discriminator for transport-level failures.
  if (!("status" in err)) return false;
  const status = (err as { status: unknown }).status;
  return (
    typeof status === "number" ||
    status === "FETCH_ERROR" ||
    status === "PARSING_ERROR" ||
    status === "TIMEOUT_ERROR" ||
    status === "CUSTOM_ERROR"
  );
}

function serializeError(
  err: unknown,
  depth = 0,
  seen: WeakSet<object> = new WeakSet<object>()
): SerializedError | unknown {
  if (err instanceof Error) {
    // Cycle + depth guard. An Error whose `.cause` chain is circular
    // (`a.cause === b; b.cause === a`) or pathologically deep would otherwise
    // recurse until the stack overflows — a RangeError thrown from inside the
    // error logger itself. Mirrors the WeakSet/MAX_DEPTH guards in
    // redact()/safeStringify() below.
    if (seen.has(err)) {
      return { name: err.name, message: err.message };
    }
    seen.add(err);
    const serialized: SerializedError = {
      name: err.name,
      message: err.message,
      stack: err.stack,
    };
    if (err.cause !== undefined && depth < MAX_DEPTH) {
      serialized.cause = serializeError(err.cause, depth + 1, seen);
    }
    return serialized;
  }
  if (isRtkQueryError(err)) {
    const status = err.status;
    if (typeof status === "number") {
      return {
        rtkQueryError: true,
        status,
        data: redact(err.data),
      };
    }
    // Transport-level RTK errors (FETCH_ERROR, PARSING_ERROR, etc.) carry
    // their detail on `.error` and never on `.data`.
    return {
      rtkQueryError: true,
      kind: status,
      data: redact((err as { error?: unknown }).error),
    };
  }
  return redact(err);
}

/**
 * Log an error with structured context.
 *
 * @param message  Human-readable description. Becomes the first console.error
 *                 argument and the Sentry event title for non-RTK errors.
 * @param error    The thrown/caught value. Handles Error instances, RTK Query
 *                 errors (status:number or transport variants), and unknown.
 * @param context  Domain context (flow name, booleans about state, etc.).
 *                 PII is redacted via the shared SENSITIVE_KEYS_WEB list.
 */
export function logError(
  message: string,
  error?: unknown,
  context?: ErrorContext
): void {
  const serializedError = serializeError(error);
  // redact returns `unknown` (because input is `unknown`), but for an object
  // input the output is always a plain object — narrow it via a cast at the
  // single boundary so callers don't have to.
  const redactedContext =
    context !== undefined
      ? (redact(context) as Record<string, unknown>)
      : undefined;

  const payload: unknown =
    redactedContext !== undefined
      ? { ...redactedContext, error: serializedError }
      : serializedError;

  // Expected user-funnel outcomes — cancellations and non-Completed OTP states
  // (FAILED / EXPIRED / EXHAUSTED) — are logged as warnings and skipped from
  // Sentry. They're not bugs; the user already got a toast. Mixpanel session
  // replay, OTel logs, and Sentry breadcrumbs still pick up console.warn for
  // diagnostics. See Sentry CUSTOMER-UI-3M (cancel) and CUSTOMER-UI-3P (OTP).
  // Fan the error out to Mixpanel as a queryable "Error Logged" event (in
  // addition to the replay-only console line above), so error causes are
  // filterable/alertable in the Activity feed — not just visible when scrubbing
  // an individual replay. No-ops when Mixpanel isn't initialized.
  const analyticsProps = summarizeErrorForAnalytics(error);

  if (error instanceof CancelledError || error instanceof OtpStateError) {
    console.warn(message, safeStringify(payload));
    trackLoggedError("warning", message, analyticsProps);
    return;
  }

  console.error(message, safeStringify(payload));
  trackLoggedError("error", message, analyticsProps);

  // RTK Query errors are already captured in baseQuery.ts — don't double-send.
  // Plain Error instances aren't captured anywhere else, so route to Sentry.
  if (error instanceof Error && !isRtkQueryError(error)) {
    Sentry.withScope((scope) => {
      if (redactedContext) scope.setContext("logError", redactedContext);
      Sentry.captureException(error);
    });
  }
}
